import { useCallback, useEffect, useState } from 'react'

import { getOverview } from '@/api/clarityLens'
import { formatMetric } from '@/hooks/clarity/util'
import { OverviewModel } from '@/types/clarityLens'

type ChartDataModel = {
  data: Record<string, number>
  labels: string[]
}

export default function useOverview(
  startDate: string,
  endDate: string,
  branches: string,
  itemName: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [supplierMixChartData, setSupplierMixChartData] =
    useState<ChartDataModel>()
  const [manufacturerBreakdownChartData, setManufacturerBreakdownChartData] =
    useState<ChartDataModel>()

  const fetchAllData = useCallback(async () => {
    if (!itemName || !startDate || !endDate) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data } = await fetchOverviewData(
        branches,
        startDate,
        endDate,
        itemName
      )

      const supplierMixChartData: ChartDataModel = data?.supplierMix
        ? {
            data: data.supplierMix,
            labels: Object.keys(data.supplierMix),
          }
        : { data: {}, labels: [] }

      const manufacturerBreakdownChartData: ChartDataModel =
        data?.manufacturerBreakdown
          ? {
              data: data.manufacturerBreakdown,
              labels: Object.keys(data.manufacturerBreakdown),
            }
          : { data: {}, labels: [] }

      setSupplierMixChartData(supplierMixChartData)
      setManufacturerBreakdownChartData(manufacturerBreakdownChartData)
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [branches, startDate, endDate, itemName])

  const refetch = useCallback(() => {
    console.log('Refetching overview data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    supplierMixChartData,
    manufacturerBreakdownChartData,
    loading,
    error,
    refetch,
  }
}

async function fetchOverviewData(
  branches: string,
  startDate: string,
  endDate: string,
  itemName: string
): Promise<{
  data: OverviewModel
}> {
  try {
    const response = await getOverview(branches, startDate, endDate, itemName)

    const data: OverviewModel = convertData(
      response.supplier_mix,
      response.manufacturer_breakdown,
      response.sku_concentration
    )

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  supplierMix: { [key: string]: number },
  manufacturerBreakdown: { [key: string]: number },
  skuConcentration: { rest: string; preferred_sku_count: number }
): OverviewModel {
  const formattedSupplierMix = Object.fromEntries(
    Object.entries(supplierMix).map(([key, value]) => [
      key,
      formatMetric(value, 2),
    ])
  )

  const formattedManufacturerBreakdown = Object.fromEntries(
    Object.entries(manufacturerBreakdown).map(([key, value]) => [
      key,
      formatMetric(value, 2),
    ])
  )

  return {
    supplierMix: formattedSupplierMix,
    manufacturerBreakdown: formattedManufacturerBreakdown,
    skuConcentration: {
      rest: skuConcentration.rest,
      preferredSkuCount: formatMetric(skuConcentration.preferred_sku_count, 2),
    },
  }
}
